import { AnNamElement } from "../../base-element";
import { customElement } from "lit/decorators.js";

/**
 * Behavior-only replacement for Dawn's SliderComponent.
 * Server-rendered children are preserved — scheduleUpdate() is suppressed.
 * Adds proper TypeScript types and disconnectedCallback cleanup.
 */
@customElement("an-slider")
export class AnSlider extends AnNamElement {
  // Suppress Lit render — preserve server-rendered children
  protected override scheduleUpdate(): void {}

  // Public surface used by SlideshowComponent-compatible callers
  enableSliderLooping = false;
  slideScrollPosition = 0;

  protected _slider: HTMLElement | null = null;
  protected _sliderItems: NodeListOf<HTMLElement> | null = null;
  protected _sliderItemsToShow: HTMLElement[] = [];
  protected _sliderItemOffset = 0;
  protected _slidesPerPage = 0;
  protected _totalPages = 0;
  protected _currentPage = 0;

  private _prevButton: HTMLElement | null = null;
  private _nextButton: HTMLElement | null = null;
  private _currentPageElement: HTMLElement | null = null;
  private _pageTotalElement: HTMLElement | null = null;
  private _resizeObserver: ResizeObserver | null = null;

  // Requires server-rendered children to be present when connectedCallback fires.
  // Dynamic injection (e.g. via innerHTML after mount) is not supported.
  override connectedCallback(): void {
    super.connectedCallback();

    this._slider = this.querySelector('[id^="Slider-"]');
    this._sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this._currentPageElement = this.querySelector(".slider-counter--current");
    this._pageTotalElement = this.querySelector(".slider-counter--total");
    this._prevButton = this.querySelector('button[name="previous"]');
    this._nextButton = this.querySelector('button[name="next"]');

    if (!this._slider || !this._prevButton || !this._nextButton) return;

    this._initPages();

    this._resizeObserver = new ResizeObserver(() => this._initPages());
    this._resizeObserver.observe(this._slider);

    this._slider.addEventListener("scroll", this._onScroll);
    this._prevButton.addEventListener("click", this._onButtonClick);
    this._nextButton.addEventListener("click", this._onButtonClick);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();

    this._resizeObserver?.disconnect();
    this._resizeObserver = null;

    this._slider?.removeEventListener("scroll", this._onScroll);
    this._prevButton?.removeEventListener("click", this._onButtonClick);
    this._nextButton?.removeEventListener("click", this._onButtonClick);
  }

  resetPages(): void {
    this._sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this._initPages();
  }

  protected _initPages(): void {
    if (!this._sliderItems) return;
    this._sliderItemsToShow = Array.from(this._sliderItems).filter(
      (el) => el.clientWidth > 0
    );
    if (this._sliderItemsToShow.length < 2) {
      // Single slide — disable both buttons so they cannot be clicked
      this._prevButton?.setAttribute("disabled", "disabled");
      this._nextButton?.setAttribute("disabled", "disabled");
      return;
    }
    this._sliderItemOffset =
      this._sliderItemsToShow[1].offsetLeft -
      this._sliderItemsToShow[0].offsetLeft;
    this._slidesPerPage = Math.round(
      (this._slider!.clientWidth - this._sliderItemsToShow[0].offsetLeft) /
        this._sliderItemOffset
    );
    this._totalPages = this._sliderItemsToShow.length - this._slidesPerPage + 1;
    this._update();
  }

  protected _update(): void {
    if (!this._slider || !this._prevButton || !this._nextButton) return;
    if (this._sliderItemOffset === 0) return;

    const previousPage = this._currentPage;
    this._currentPage =
      Math.round(this._slider.scrollLeft / this._sliderItemOffset) + 1;

    if (this._currentPageElement && this._pageTotalElement) {
      this._currentPageElement.textContent = String(this._currentPage);
      this._pageTotalElement.textContent = String(this._totalPages);
    }

    if (this._currentPage !== previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this._currentPage,
            currentElement: this._sliderItemsToShow[this._currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this._isSlideVisible(this._sliderItemsToShow[0]) &&
      this._slider.scrollLeft === 0
    ) {
      this._prevButton.setAttribute("disabled", "disabled");
      this._slider.classList.remove("slider--has-left");
    } else {
      this._prevButton.removeAttribute("disabled");
      this._slider.classList.add("slider--has-left");
    }

    if (
      this._isSlideVisible(
        this._sliderItemsToShow[this._sliderItemsToShow.length - 1]
      )
    ) {
      this._nextButton.setAttribute("disabled", "disabled");
      this._slider.classList.remove("slider--has-right");
    } else {
      this._nextButton.removeAttribute("disabled");
      this._slider.classList.add("slider--has-right");
    }
  }

  protected _isSlideVisible(element: HTMLElement, offset = 0): boolean {
    const lastVisibleSlide =
      this._slider!.clientWidth + this._slider!.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this._slider!.scrollLeft
    );
  }

  private _onScroll = (): void => {
    this._update();
  };

  private _onButtonClick = (event: Event): void => {
    event.preventDefault();
    const btn = event.currentTarget as HTMLElement;
    const step = parseInt(btn.dataset.step ?? "1") || 1;
    this.slideScrollPosition =
      btn.getAttribute("name") === "next"
        ? this._slider!.scrollLeft + step * this._sliderItemOffset
        : this._slider!.scrollLeft - step * this._sliderItemOffset;
    this._setSlidePosition(this.slideScrollPosition);
  };

  protected _setSlidePosition(position: number): void {
    this._slider?.scrollTo({ left: position });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "an-slider": AnSlider;
  }
}
